/* soolla-mesh.js — <soolla-mesh> animated gradient-mesh background. Zero dependencies.
   Theme integration (2026-09-29): adds class "is-ready" + event "soolla-mesh:ready" after the first painted frame so the host can fade it in.
   A WebGL fragment shader paints a slow, domain-warped 3-colour field over a paper base
   with the brand's diagonal white light sweep. Colour changes crossfade smoothly.

   <soolla-mesh colors="#7F9DE0,#F0949F,#E4EBFA" paper="#F9F8F6" strength="0.62" speed="1" sweep="0.85"></soolla-mesh>

   colors    3 hex colours (pool A top-left, pool B right, pool C bottom-centre)
   paper     base colour the pools melt into            default #F9F8F6
   strength  0–1, how much colour vs paper              default 0.62
   speed     time multiplier (1 ≈ 25–40s drift cycles)  default 1
   sweep     0–1, light-sweep intensity                 default 0.85
   Size it with CSS (it fills its box). Pauses offscreen / in background tabs;
   holds a still frame under prefers-reduced-motion; CSS gradient fallback without WebGL. */
(function () {
  if (customElements.get("soolla-mesh")) return;

  var VS = "attribute vec2 a;void main(){gl_Position=vec4(a,0.,1.);}";
  var FS = [
    "precision highp float;",
    "uniform vec2 r;uniform float t;uniform vec3 P,A,B,C;uniform float S,W;",
    "float h(vec2 p){return fract(sin(dot(p,vec2(12.9898,78.233)))*43758.5453);}",
    "float g(vec2 q,vec2 c,float k){vec2 d=q-c;return exp(-dot(d,d)/(k*k));}",
    "void main(){",
    " vec2 uv=gl_FragCoord.xy/r;uv.y=1.-uv.y;",
    " float as=r.x/r.y;vec2 p=vec2(uv.x*as,uv.y);",
    " vec2 q=p;",
    " q+=.20*vec2(sin(q.y*1.55+t*.21),cos(q.x*1.35-t*.17));",
    " q+=.11*vec2(sin(q.y*2.60-t*.15+1.3),cos(q.x*2.20+t*.19+.7));",
    " q+=.05*vec2(sin(q.y*4.10+t*.11+2.1),cos(q.x*3.70-t*.13+.4));",
    " vec2 ca=vec2(as*(.12+.08*sin(t*.10)),.10+.10*cos(t*.12));",
    " vec2 cb=vec2(as*(.88+.07*cos(t*.09+1.)),.32+.12*sin(t*.11+2.));",
    " vec2 cc=vec2(as*(.50+.14*sin(t*.07+4.)),.86+.08*cos(t*.09+1.));",
    " float s=.5*max(as,1.);",
    " float wa=g(q,ca,.95*s),wb=g(q,cb,.82*s),wc=g(q,cc,.78*s),wp=.32;",
    " vec3 col=(P*wp+A*wa+B*wb+C*wc)/(wp+wa+wb+wc);",
    " col=mix(P,col,S);",
    " vec2 N=vec2(.225,.974);",
    " vec2 sc=vec2(as*(.5+.10*sin(t*.06)),.54+.05*cos(t*.08));",
    " float d=dot(q-sc,N);",
    " float band=exp(-d*d/(.018+.006*sin(t*.09)));",
    " col=mix(col,vec3(1.),band*W*.82);",
    " col+=(h(gl_FragCoord.xy+fract(t))-.5)/255.;",
    " gl_FragColor=vec4(col,1.);",
    "}"
  ].join("\n");

  function hex(v) {
    v = String(v || "").trim().replace("#", "");
    if (v.length === 3) v = v.split("").map(function (c) { return c + c; }).join("");
    var n = parseInt(v, 16);
    if (isNaN(n)) return [0.976, 0.973, 0.965];
    return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
  }
  var reduceMQ = window.matchMedia ? window.matchMedia("(prefers-reduced-motion: reduce)") : null;

  class SoollaMesh extends HTMLElement {
    static get observedAttributes() { return ["colors", "paper", "strength", "speed", "sweep"]; }
    constructor() {
      super();
      var root = this.attachShadow({ mode: "open" });
      root.innerHTML = "<style>:host{display:block;position:relative;overflow:hidden}canvas{position:absolute;inset:0;width:100%;height:100%;display:block}</style><canvas part=\"canvas\"></canvas>";
      this.cv = root.querySelector("canvas");
      this.t = 30 + Math.random() * 20;
      this.raf = 0; this.last = 0; this.visible = true;
      this._frame = this._frame.bind(this);
      this._wake = this._wake.bind(this);
    }
    _read() {
      var cs = (this.getAttribute("colors") || "#B9DBF7,#FFB0EE,#F7F1EE").split(",");
      while (cs.length < 3) cs.push(cs[cs.length - 1]);
      var o = [].concat(hex(this.getAttribute("paper") || "#F9F8F6"), hex(cs[0]), hex(cs[1]), hex(cs[2]));
      var s = parseFloat(this.getAttribute("strength")), w = parseFloat(this.getAttribute("sweep"));
      o.push(isNaN(s) ? 0.62 : s, isNaN(w) ? 0.85 : w);
      this.speed = parseFloat(this.getAttribute("speed")); if (isNaN(this.speed)) this.speed = 1;
      return o;
    }
    attributeChangedCallback() {
      this.tgt = this._read();
      if (!this.cur) this.cur = this.tgt.slice();
      if (!this.gl) this._fallback();
      this._wake();
    }
    connectedCallback() {
      this.tgt = this._read(); if (!this.cur) this.cur = this.tgt.slice();
      this._initGL();
      var self = this;
      this.ro = new ResizeObserver(function () { self._resize(); self._wake(); });
      this.ro.observe(this);
      this.io = new IntersectionObserver(function (e) { self.visible = e[0].isIntersecting; self._wake(); });
      this.io.observe(this);
      document.addEventListener("visibilitychange", this._wake);
      this._resize(); this._wake();
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.raf); this.raf = 0;
      if (this.ro) this.ro.disconnect(); if (this.io) this.io.disconnect();
      document.removeEventListener("visibilitychange", this._wake);
    }
    _initGL() {
      if (this.gl) return;
      var gl = this.cv.getContext("webgl", { antialias: false, alpha: false, premultipliedAlpha: false, preserveDrawingBuffer: false });
      if (!gl) { this._fallback(); return; }
      function sh(type, src) { var s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s); return s; }
      var pr = gl.createProgram();
      gl.attachShader(pr, sh(gl.VERTEX_SHADER, VS)); gl.attachShader(pr, sh(gl.FRAGMENT_SHADER, FS));
      gl.linkProgram(pr);
      if (!gl.getProgramParameter(pr, gl.LINK_STATUS)) { this._fallback(); return; }
      gl.useProgram(pr);
      var b = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, b);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
      var loc = gl.getAttribLocation(pr, "a"); gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
      this.u = {}; var self = this;
      ["r", "t", "P", "A", "B", "C", "S", "W"].forEach(function (k) { self.u[k] = gl.getUniformLocation(pr, k); });
      this.gl = gl;
    }
    _fallback() {
      if (!this.ready) { this.ready = true; this.classList.add("is-ready"); this.dispatchEvent(new CustomEvent("soolla-mesh:ready", { bubbles: true })); }
      var c = (this.getAttribute("colors") || "").split(",");
      this.style.background = "radial-gradient(60% 55% at 12% 10%," + (c[0] || "#B9DBF7") + "99,transparent 70%),radial-gradient(50% 50% at 88% 32%," + (c[1] || c[0]) + "88,transparent 70%),radial-gradient(45% 45% at 50% 86%," + (c[2] || c[0]) + "80,transparent 70%)," + (this.getAttribute("paper") || "#F9F8F6");
    }
    _resize() {
      if (!this.gl) return;
      /* the field is ultra-smooth, so half-res (capped) is visually identical and ~4x cheaper */
      var w = this.clientWidth, h = this.clientHeight, k = Math.min(0.5, 900 / Math.max(1, w, h));
      this.cv.width = Math.max(2, Math.round(w * k)); this.cv.height = Math.max(2, Math.round(h * k));
      this.gl.viewport(0, 0, this.cv.width, this.cv.height);
    }
    _wake() {
      if (!this.raf && this.gl && this.isConnected) { this.last = 0; this.raf = requestAnimationFrame(this._frame); }
    }
    _frame(ts) {
      this.raf = 0;
      var dt = this.last ? Math.min(0.1, (ts - this.last) / 1000) : 0; this.last = ts;
      var still = reduceMQ && reduceMQ.matches;
      if (!still) this.t += dt * this.speed;
      var k = 1 - Math.exp(-dt / 0.28), moving = false;
      for (var i = 0; i < this.cur.length; i++) {
        var d = this.tgt[i] - this.cur[i];
        if (Math.abs(d) > 0.0015) { this.cur[i] += d * k; moving = true; } else this.cur[i] = this.tgt[i];
      }
      var gl = this.gl, u = this.u, c = this.cur;
      gl.uniform2f(u.r, this.cv.width, this.cv.height); gl.uniform1f(u.t, this.t);
      gl.uniform3f(u.P, c[0], c[1], c[2]); gl.uniform3f(u.A, c[3], c[4], c[5]);
      gl.uniform3f(u.B, c[6], c[7], c[8]); gl.uniform3f(u.C, c[9], c[10], c[11]);
      gl.uniform1f(u.S, c[12]); gl.uniform1f(u.W, c[13]);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      if (!this.ready) { this.ready = true; this.classList.add("is-ready"); this.dispatchEvent(new CustomEvent("soolla-mesh:ready", { bubbles: true })); }
      var run = this.visible && !document.hidden && (!still || moving || dt === 0);
      if (run) this.raf = requestAnimationFrame(this._frame);
    }
  }
  customElements.define("soolla-mesh", SoollaMesh);
})();
